import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Eu } from '../../acesso';
import type { Sessao } from '../../auth';
import { ErroDeArea } from '../../design/Estados';
import { FotoDaConta } from '../../design/FotoDePerfil';
import { Icone } from '../../design/Icone';
import {
  composicao,
  desfazerCorte,
  diaCurto,
  estornar,
  lerSemana,
  liberar,
  MOTIVO_DO_CORTE,
  mesPorExtenso,
  pagar,
  reais,
  reaisCurto,
  semanaCurta,
  vendasSemPessoaPendentes,
} from '../../premiacao';
import { lerHistoricoDePagamentos } from '../../premiacao';
import type {
  ChequeDaPessoa,
  ChequeDaSemana,
  Escrita,
  Pagamento,
  PagamentoDoHistorico,
} from '../../premiacao';
import { baixarCsv } from '../../design/csv';
import { EscolherModelo, FolhasA4 } from './FolhasA4';
import type { ModeloDaFolha } from './FolhasA4';
import { diaCurtoEmBrasilia } from '../../design/datas';
import { EMPRESA, dataDoRecibo } from '../../design/empresa';
import { valorPorExtenso } from '../../design/extenso';
import { linkDasVendas } from '../inovapay/link-das-vendas';
import { porQueNaoPaga, resumoDaLiberacao } from './confirmacoes';
import './premiacao.css';
import './conferencia.css';
import { semanaDoDia } from '../../design/semana';
import { VendasDaPessoa } from './VendasDaPessoa';

/**
 * TELA 10 (admin) — PREMIAÇÃO DA EQUIPE. "Reconhecimento adulto: número claro, origem explicada."
 *
 * Tudo vem do cheque calculado no servidor (paridade com o 1.0). Status de cada pessoa:
 *   Pago dd/mm        há pagamento vigente deste período
 *   Em apuração       a semana ainda não fechou (sexta às 23:59)
 *   A pagar           fechou e tem valor a receber
 *   Meta não batida   o a receber zerou pela meta (o que ela fez continua à vista)
 *   Sem saldo         nada a receber (ou devolução maior que o ganho)
 */

function hojeBrasilia(): string {
  return new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });
}
const semanaDe = semanaDoDia;

type Status = { rotulo: string; tipo: 'pago' | 'apuracao' | 'pagar' | 'bloqueado' | 'zero' };
function statusDe(c: ChequeDaPessoa, semana: ChequeDaSemana, fechada: boolean): Status {
  const p = semana.pagamentos[c.pessoaId];
  if (p) return { rotulo: `Pago ${diaCurtoEmBrasilia(p.pagoEm)}`, tipo: 'pago' };
  // Tela 24 C: retida por regra de qualidade do Giro (o servidor também recusa o pagamento).
  if (semana.retidas?.[c.pessoaId]) return { rotulo: 'Retida', tipo: 'bloqueado' };
  if (c.zeradoPorMeta) return { rotulo: 'Meta não batida', tipo: 'bloqueado' };
  if (c.aReceber <= 0) return { rotulo: 'Sem saldo', tipo: 'zero' };
  return fechada
    ? { rotulo: 'A pagar', tipo: 'pagar' }
    : { rotulo: 'Em apuração', tipo: 'apuracao' };
}

const itensDe = (c: ChequeDaPessoa) => c.linhas.bate.reduce((s, l) => s + l.qtd, 0);

const maisDias = (d: string, n: number) =>
  new Date(Date.parse(`${d}T12:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);

export function Premiacao({
  sessao,
  eu,
  parametros,
}: {
  sessao: Sessao;
  eu: Eu | null;
  parametros?: URLSearchParams;
}) {
  const hoje = hojeBrasilia();
  const atual = semanaDe(hoje);
  // Qualquer semana (?semana=AAAA-MM-DD): a semana sem pagamento lá atrás também se paga daqui.
  const pedida = parametros?.get('semana') ?? null;
  const periodo =
    pedida && /^\d{4}-\d{2}-\d{2}$/.test(pedida) && pedida <= hoje ? semanaDe(pedida) : atual;
  const ehAtual = periodo.de === atual.de;
  const irSemana = (de: string) => {
    window.location.hash = de === atual.de ? '#/premiacao' : `#/premiacao?semana=${de}`;
  };
  const [dados, setDados] = useState<ChequeDaSemana | null>(null);
  const [erro, setErro] = useState(false);
  const [busca, setBusca] = useState('');
  const [aberta, setAberta] = useState<string | null>(null);
  const [pagando, setPagando] = useState(false);
  // folhas A4 (4 modelos): escolhe o modelo, imprime, some
  const [escolher, setEscolher] = useState(false);
  const [imprimir, setImprimir] = useState<ModeloDaFolha | null>(null);
  useEffect(() => {
    if (!imprimir) return;
    const fim = () => setImprimir(null);
    window.addEventListener('afterprint', fim);
    const t = window.setTimeout(() => window.print(), 80);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('afterprint', fim);
    };
  }, [imprimir]);
  const podePagar = eu?.capacidades.includes('premiacao.pagar') ?? false;
  const podeConferir = eu?.capacidades.includes('premiacao.conferir') ?? false;

  // `manter`: relê sem apagar a tela — a gaveta aberta (e o aviso dela) não some no meio.
  const carregar = useCallback(
    (manter = false) => {
      setErro(false);
      if (!manter) setDados(null);
      void lerSemana(periodo.de, periodo.ate).then((r) => {
        if (r.ok) setDados(r.dados);
        else setErro(true);
      });
    },
    [periodo.de, periodo.ate],
  );
  useEffect(() => carregar(), [carregar]);

  const fechada = periodo.ate < hoje;
  // a leitura do HOS que ainda não passou do último dia: o servidor recusa, a tela explica
  const leituraPendente = dados?.fonteDoHos?.leituraPendente ?? null;
  const naoPaga = porQueNaoPaga({ fechada, leituraPendente });
  const pessoas = useMemo(() => {
    const t = busca.trim().toLowerCase();
    return (dados?.pessoas ?? []).filter((p) => !t || p.nome.toLowerCase().includes(t));
  }, [dados, busca]);

  const kpi = useMemo(() => {
    if (!dados) return null;
    const aPagar = dados.pessoas.filter((p) => p.aReceber > 0 && !dados.pagamentos[p.pessoaId]);
    const semRelatorio = new Set(dados.avisos.diasSemRelatorio);
    const aguardando = dados.pessoas.flatMap((p) =>
      p.linhas.naoVendeu.filter((l) => semRelatorio.has(l.dia)),
    );
    const deduzidos = dados.pessoas.flatMap((p) => [
      ...p.linhas.canceladas.filter((l) => l.desconto > 0),
      ...p.linhas.devolvidos,
    ]);
    return {
      aPagar: aPagar.reduce((s, p) => s + p.aReceber, 0),
      pessoasAPagar: aPagar.length,
      apuracao: aguardando.reduce((s, l) => s + l.pago, 0),
      itensAguardando: aguardando.reduce((s, l) => s + l.qtd, 0),
      deduzido: dados.pessoas.reduce((s, p) => s + p.descontado, 0),
      itensDeduzidos: deduzidos.reduce((s, l) => s + l.qtd, 0),
    };
  }, [dados]);

  const exportar = () => {
    if (!dados) return;
    baixarCsv(`premiacao-${periodo.de}-a-${periodo.ate}.csv`, [
      [
        'Balconista',
        'Código',
        'Itens',
        'Vitaminas',
        'Pré-vencidos',
        'Paradão',
        'Deduzido',
        'A receber',
        'Status',
      ],
      ...dados.pessoas.map((p) => [
        p.nome,
        p.codigo ?? '',
        String(itensDe(p)),
        p.tipos.vitamina.bruto.toFixed(2).replace('.', ','),
        p.tipos.pre_vencido.bruto.toFixed(2).replace('.', ','),
        p.tipos.paradao.bruto.toFixed(2).replace('.', ','),
        p.descontado.toFixed(2).replace('.', ','),
        p.aReceber.toFixed(2).replace('.', ','),
        statusDe(p, dados, fechada).rotulo,
      ]),
    ]);
  };

  const pessoaAberta = dados?.pessoas.find((p) => p.pessoaId === aberta) ?? null;

  return (
    <section className="pr">
      <div className="pr-topo">
        <div>
          <h1>Premiação</h1>
          <div className="pr-sub">
            Semana {semanaCurta(periodo.de, periodo.ate)} ·{' '}
            {fechada ? 'semana fechada' : 'valores em apuração até sexta às 23:59'}
          </div>
        </div>
        <div className="pr-acoes">
          <div className="cf-navega" role="group" aria-label="Semana">
            <button
              type="button"
              aria-label="Semana anterior"
              onClick={() => irSemana(semanaDe(maisDias(periodo.de, -1)).de)}
            >
              <span className="cf-seta-esq">
                <Icone nome="chev-r" tamanho={14} traco={1.8} />
              </span>
            </button>
            <strong>{semanaCurta(periodo.de, periodo.ate)}</strong>
            <button
              type="button"
              aria-label="Próxima semana"
              disabled={ehAtual}
              onClick={() => irSemana(semanaDe(maisDias(periodo.ate, 1)).de)}
            >
              <Icone nome="chev-r" tamanho={14} traco={1.8} />
            </button>
            {!ehAtual && (
              <button type="button" className="cf-navega-hoje" onClick={() => irSemana(atual.de)}>
                Semana atual
              </button>
            )}
          </div>
          {podeConferir && (
            <a className="pr-botao" href={`#/conferencia?semana=${periodo.de}`}>
              Conferência
            </a>
          )}
          <button type="button" className="pr-botao" onClick={exportar} disabled={!dados}>
            Exportar
          </button>
          <button
            type="button"
            className="pr-botao"
            onClick={() => setEscolher(true)}
            disabled={!dados || pessoas.length === 0}
            title="Cheques/comprovantes em folha A4 de quem está na lista: 4 modelos"
          >
            <Icone nome="file" tamanho={13} traco={1.8} /> Imprimir folhas
          </button>
          {podePagar && (
            <button
              type="button"
              className="pr-botao pr-botao-vermelho"
              onClick={() => setPagando(true)}
              disabled={!dados || !kpi || kpi.pessoasAPagar === 0 || naoPaga !== null}
              title={naoPaga ?? undefined}
            >
              Registrar pagamento
            </button>
          )}
        </div>
      </div>

      {erro ? (
        <ErroDeArea oQue="a premiação" aoTentar={() => carregar()} />
      ) : (
        <>
          <div className="pr-kpis">
            <div className="pr-kpi pr-kpi-ouro">
              <div className="pr-kpi-rotulo">{fechada ? 'A PAGAR' : 'A PAGAR NO SÁBADO'}</div>
              <div className="pr-kpi-valor">{kpi ? reaisCurto(kpi.aPagar) : '—'}</div>
              <div className="pr-kpi-sub">
                {kpi
                  ? `${String(kpi.pessoasAPagar)} ${kpi.pessoasAPagar === 1 ? 'balconista' : 'balconistas'}`
                  : ' '}
              </div>
            </div>
            <div className="pr-kpi">
              <div className="pr-kpi-rotulo">EM APURAÇÃO</div>
              <div className="pr-kpi-valor">{kpi ? reaisCurto(kpi.apuracao) : '—'}</div>
              <div className="pr-kpi-sub">
                {kpi
                  ? `${String(kpi.itensAguardando)} ${kpi.itensAguardando === 1 ? 'item aguarda' : 'itens aguardam'} HOS`
                  : ' '}
              </div>
            </div>
            <div className="pr-kpi">
              <div className="pr-kpi-rotulo">DEDUZIDO</div>
              <div className="pr-kpi-valor pr-vermelho">
                {kpi ? (kpi.deduzido > 0 ? `−${reaisCurto(kpi.deduzido)}` : 'R$ 0') : '—'}
              </div>
              <div className="pr-kpi-sub">
                {kpi
                  ? `${String(kpi.itensDeduzidos)} ${kpi.itensDeduzidos === 1 ? 'item cancelado ou devolvido' : 'itens cancelados ou devolvidos'}`
                  : ' '}
              </div>
            </div>
            <div className="pr-kpi">
              <div className="pr-kpi-rotulo">PAGO NO MÊS</div>
              <div className="pr-kpi-valor">
                {dados ? reaisCurto(Number(dados.pagoNoMes.valor)) : '—'}
              </div>
              <div className="pr-kpi-sub">
                {dados
                  ? `${String(dados.pagoNoMes.semanas)} ${dados.pagoNoMes.semanas === 1 ? 'semana fechada' : 'semanas fechadas'}`
                  : ' '}
              </div>
            </div>
          </div>

          {dados &&
            (vendasSemPessoaPendentes(dados).length > 0 ||
              dados.avisos.diasSemRelatorio.length > 0) && (
              <div className="pr-aviso" role="status">
                <Icone nome="warn" tamanho={15} traco={1.7} />
                <span>
                  {dados.avisos.diasSemRelatorio.length > 0 &&
                    `${String(dados.avisos.diasSemRelatorio.length)} ${dados.avisos.diasSemRelatorio.length === 1 ? 'dia' : 'dias'} sem venda do HOS. `}
                  {vendasSemPessoaPendentes(dados).length > 0 &&
                    `${String(vendasSemPessoaPendentes(dados).reduce((s, v) => s + v.vendas, 0))} vendas de nomes do HOS ainda sem pessoa confirmada — não pagam ninguém até alguém confirmar.`}
                </span>
                {podeConferir && <a href="#/conferencia">Resolver na conferência</a>}
              </div>
            )}

          {podePagar && fechada && leituraPendente && (
            <div className="pr-aviso" role="status">
              <Icone nome="warn" tamanho={15} traco={1.7} />
              <span>{leituraPendente}</span>
            </div>
          )}

          <div className="pr-tabela">
            <div className="pr-tabela-topo">
              <h2>Por balconista</h2>
              <span>
                {dados
                  ? `${String(dados.pessoas.filter((p) => p.devidoTotal > 0).length)} com premiação nesta semana`
                  : ''}
              </span>
              <div style={{ flex: 1 }} />
              <label className="pr-busca">
                <Icone nome="search" tamanho={14} traco={1.6} />
                <input
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  placeholder="Buscar pessoa…"
                  aria-label="Buscar pessoa"
                />
              </label>
            </div>
            <div className="pr-rolagem">
              <table>
                <thead>
                  <tr>
                    <th className="pr-esq">BALCONISTA</th>
                    <th>ITENS</th>
                    <th>VITAMINAS</th>
                    <th>PRÉ-VENCIDOS</th>
                    <th>PARADÃO</th>
                    <th>DEDUZIDO</th>
                    <th className="pr-forte">TOTAL</th>
                    <th className="pr-esq">STATUS</th>
                    <th aria-label="Ações" />
                  </tr>
                </thead>
                <tbody>
                  {dados === null &&
                    [0, 1, 2, 3].map((i) => (
                      <tr key={i} className="pr-esqueleto">
                        <td colSpan={9}>
                          <span />
                        </td>
                      </tr>
                    ))}
                  {dados !== null && pessoas.length === 0 && (
                    <tr>
                      <td colSpan={9} className="pr-vazio">
                        {busca
                          ? 'Ninguém com esse nome nesta semana.'
                          : 'Nenhuma venda conferida nesta semana ainda.'}
                      </td>
                    </tr>
                  )}
                  {dados !== null &&
                    pessoas.map((p) => {
                      const st = statusDe(p, dados, fechada);
                      return (
                        <tr
                          key={p.pessoaId}
                          className={aberta === p.pessoaId ? 'pr-aberta' : ''}
                          onClick={() => setAberta(p.pessoaId)}
                        >
                          <td className="pr-esq pr-nome">
                            <a
                              href={`#/perfil?pessoa=${p.pessoaId}`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {p.nome}
                            </a>
                          </td>
                          <td>{itensDe(p)}</td>
                          <td>
                            {p.tipos.vitamina.bruto > 0 ? reais(p.tipos.vitamina.bruto) : '—'}
                          </td>
                          <td>
                            {p.tipos.pre_vencido.bruto > 0 ? reais(p.tipos.pre_vencido.bruto) : '—'}
                          </td>
                          <td>{p.tipos.paradao.bruto > 0 ? reais(p.tipos.paradao.bruto) : '—'}</td>
                          <td className={p.descontado > 0 ? 'pr-vermelho' : 'pr-traco'}>
                            {p.descontado > 0 ? `−${reais(p.descontado)}` : '—'}
                          </td>
                          <td className="pr-total">{reais(p.aReceber)}</td>
                          <td className="pr-esq">
                            <span className={`pr-status pr-status-${st.tipo}`}>{st.rotulo}</span>
                          </td>
                          <td>
                            <button
                              type="button"
                              className="pr-mais"
                              aria-label={`Detalhes de ${p.nome}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setAberta(p.pessoaId);
                              }}
                            >
                              <Icone nome="dots" tamanho={16} traco={1.8} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
            <div className="pr-tabela-rodape">
              <span>
                {dados
                  ? `${String(pessoas.length)} de ${String(dados.pessoas.length)} na tela`
                  : ''}
              </span>
              <strong>{dados ? `Total da semana: ${reais(dados.totais.aReceber)}` : ''}</strong>
            </div>
          </div>
          <PagamentosAnteriores irSemana={irSemana} />
        </>
      )}

      {pessoaAberta && dados && (
        <GavetaDaPessoa
          c={pessoaAberta}
          semana={dados}
          fechada={fechada}
          leituraPendente={leituraPendente}
          sessao={sessao}
          podePagar={podePagar}
          podeConferir={podeConferir}
          aoFechar={() => setAberta(null)}
          aoMudar={() => carregar(true)}
        />
      )}
      {escolher && (
        <EscolherModelo
          quantas={pessoas.length}
          aoFechar={() => setEscolher(false)}
          aoImprimir={(m) => {
            setImprimir(m);
            setEscolher(false);
          }}
        />
      )}
      {dados &&
        imprimir &&
        createPortal(
          <FolhasA4
            modelo={imprimir}
            pessoas={pessoas}
            semana={dados}
            semRelatorio={new Set(dados.avisos.diasSemRelatorio)}
          />,
          document.body,
        )}
      {pagando && dados && (
        <ModalDePagamento
          semana={dados}
          sessao={sessao}
          aoFechar={() => setPagando(false)}
          aoTerminar={() => {
            setPagando(false);
            carregar();
          }}
        />
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * Gaveta: a premiação de uma pessoa
 * ------------------------------------------------------------------ */

function GavetaDaPessoa({
  c,
  semana,
  fechada,
  leituraPendente,
  sessao,
  podePagar,
  podeConferir,
  aoFechar,
  aoMudar,
}: {
  c: ChequeDaPessoa;
  semana: ChequeDaSemana;
  fechada: boolean;
  leituraPendente: string | null;
  sessao: Sessao;
  podePagar: boolean;
  podeConferir: boolean;
  aoFechar: () => void;
  aoMudar: () => void;
}) {
  const st = statusDe(c, semana, fechada);
  const pago = semana.pagamentos[c.pessoaId] ?? null;
  const linhas = composicao(c);
  const [acao, setAcao] = useState<
    | null
    | { tipo: 'liberar'; ids: readonly string[]; nome: string }
    | { tipo: 'estornar' }
    | { tipo: 'desfazerCorte'; id: string; mes: string }
    | { tipo: 'comprovante' }
    | { tipo: 'pagar' }
  >(null);
  const [resultado, setResultado] = useState<Escrita | null>(null);
  const [trabalhando, setTrabalhando] = useState(false);
  const vendas =
    c.linhas.bate.reduce((s, l) => s + l.vendaIds.length, 0) +
    c.linhas.naoVendeu.reduce((s, l) => s + l.vendaIds.length, 0);

  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && aoFechar();
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [aoFechar]);

  const naoPaga = porQueNaoPaga({ fechada, leituraPendente });
  const pagarAgora = async () => {
    setTrabalhando(true);
    const r = await pagar(sessao, c.pessoaId, semana.periodo.de, semana.periodo.ate);
    setTrabalhando(false);
    setResultado(r);
    setAcao(null);
    if (r.ok) aoMudar();
  };

  const foraDaConta = [
    ...c.linhas.abaixoDoCard.map((l) => ({
      l,
      tipo: 'abaixo' as const,
      texto: l.motivo ?? 'abaixo do card',
      valor: l.pago,
    })),
    ...c.linhas.diaErrado.map((l) => ({
      l,
      tipo: 'dia' as const,
      texto: `lançou em ${diaCurto(l.lancouEm)}, vendeu em ${diaCurto(l.dia)}`,
      valor: l.perdeu,
    })),
    ...(c.linhas.loteDoMesmoDia ?? []).map((l) => ({
      l,
      tipo: 'lote' as const,
      texto: `pré-vencido vendido no dia em que o lote entrou (${diaCurto(l.loteCriadoEm)})`,
      valor: l.perdeu,
    })),
  ];

  return (
    <>
      <div className="pr-veu" onClick={aoFechar} aria-hidden="true" />
      <aside className="pr-gaveta cf-gaveta" role="dialog" aria-label={`Premiação de ${c.nome}`}>
        <div className="pr-gaveta-topo">
          <div className="pr-gaveta-pessoa">
            <span className="pr-avatar">
              <FotoDaConta accountId={c.pessoaId} nome={c.nome} />
            </span>
            <div>
              <div className="pr-gaveta-nome">{c.nome}</div>
              <div className="pr-gaveta-sub">
                cód {c.codigo ?? '—'} · semana {semanaCurta(semana.periodo.de, semana.periodo.ate)}
              </div>
            </div>
          </div>
          <button type="button" className="pr-fechar" onClick={aoFechar} aria-label="Fechar">
            <Icone nome="x" tamanho={16} traco={1.7} />
          </button>
        </div>
        <div className="pr-gaveta-corpo">
          <div className="pr-destaque">
            <div className="pr-destaque-topo">
              <span>PREMIAÇÃO DA SEMANA</span>
              <span className={`pr-selo pr-status-${st.tipo}`}>{st.rotulo}</span>
            </div>
            <div className="pr-destaque-valor">{reais(c.aReceber)}</div>
            {c.zeradoPorMeta && (
              <div className="pr-destaque-nota">
                Não bateu a meta de {c.meta} itens (fez {c.itensLiquidos}; faltaram{' '}
                {c.faltouParaMeta}). Deixou de receber{' '}
                {reais(Math.max(0, c.devidoTotal - c.devolucao))}.
              </div>
            )}
          </div>

          {(c.cortes ?? []).map((k) => (
            <div key={k.id} className="pr-corte" role="status">
              <div>
                <strong>Vitamina de {mesPorExtenso(k.mes)} cortada</strong> ·{' '}
                {MOTIVO_DO_CORTE[k.tipo]}
                <span>{k.detalhe}</span>
                {k.descontoDeSemanaPaga > 0 && (
                  <span>
                    Vitamina desse mês já paga: −{reais(k.descontoDeSemanaPaga)} neste pagamento.
                  </span>
                )}
              </div>
              {podePagar && (
                <button
                  type="button"
                  className="pr-botao"
                  onClick={() => setAcao({ tipo: 'desfazerCorte', id: k.id, mes: k.mes })}
                >
                  Desfazer
                </button>
              )}
            </div>
          ))}

          <div className="pr-secao">COMPOSIÇÃO</div>
          {linhas.length === 0 ? (
            <p className="pr-nada">Nenhuma venda conferida ainda.</p>
          ) : (
            <table data-sem-ordenar className="pr-composicao">
              <thead>
                <tr>
                  <th className="pr-esq">PRODUTO</th>
                  <th>QTD</th>
                  <th className="pr-esq pr-regra">REGRA</th>
                  <th>GERADO</th>
                </tr>
              </thead>
              <tbody>
                {linhas.map((l, i) => (
                  <tr key={i} className={l.desconto ? 'pr-linha-desconto' : ''}>
                    <td className="pr-esq">{l.desconto ? <s>{l.produto}</s> : l.produto}</td>
                    <td>{l.qtd}</td>
                    <td className="pr-esq pr-regra">{l.regra}</td>
                    <td className="pr-gerado">{reais(l.gerado)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="pr-total-apurado">
            <span>Total apurado</span>
            <strong>{reais(c.aReceber)}</strong>
          </div>

          {(foraDaConta.length > 0 ||
            c.linhas.naoVendeu.length > 0 ||
            c.linhas.naoLancou.length > 0) && (
            <>
              <div className="pr-secao">NÃO ENTROU NA CONTA</div>
              <ul className="pr-fora">
                {foraDaConta.map(({ l, tipo, texto, valor }, i) => (
                  <li key={`f${String(i)}`}>
                    <div>
                      <strong>{l.produto.nome}</strong> · {l.qtd} un · {diaCurto(l.dia)}
                      <span>{texto}</span>
                    </div>
                    <div className="pr-fora-acao">
                      <em>
                        {tipo === 'abaixo' ? `app: ${reais(valor)}` : `perdeu ${reais(valor)}`}
                      </em>
                      {podeConferir && !pago && l.vendaIds[0] && (
                        <button
                          type="button"
                          onClick={() =>
                            setAcao({ tipo: 'liberar', ids: l.vendaIds, nome: l.produto.nome })
                          }
                        >
                          Liberar
                        </button>
                      )}
                    </div>
                  </li>
                ))}
                {c.linhas.naoVendeu.map((l, i) => (
                  <li key={`v${String(i)}`}>
                    <div>
                      <strong>{l.produto.nome}</strong> · {l.qtd} un · {diaCurto(l.dia)}
                      <span>
                        {semana.avisos.diasSemRelatorio.includes(l.dia)
                          ? 'aguardando a venda do HOS deste dia'
                          : 'lançado sem venda no HOS'}
                      </span>
                    </div>
                    <em>app: {reais(l.pago)}</em>
                  </li>
                ))}
                {c.linhas.naoLancou.map((l, i) => (
                  <li key={`n${String(i)}`}>
                    <div>
                      <strong>{l.produto.nome}</strong> · {l.qtd} un · {diaCurto(l.dia)}
                      <span>vendeu no HOS e não lançou</span>
                    </div>
                    <em>deixou de ganhar {reais(l.perdeu)}</em>
                  </li>
                ))}
              </ul>
            </>
          )}

          <div className="pr-secao">VENDAS DA SEMANA</div>
          <VendasDaPessoa
            c={c}
            semRelatorio={new Set(semana.avisos.diasSemRelatorio)}
            sessao={sessao}
            podeCancelar={podeConferir}
            podeEditar={podeConferir}
            aoMudar={aoMudar}
          />

          {!pago && semana.retidas?.[c.pessoaId] && (
            <p className="pr-recusado" role="note">
              Premiação retida por regra de qualidade do Giro desde{' '}
              {new Date(semana.retidas[c.pessoaId]?.desde ?? '').toLocaleDateString('pt-BR', {
                timeZone: 'America/Sao_Paulo',
              })}
              : {semana.retidas[c.pessoaId]?.porque}.{' '}
              <a href={`#/qualidade?pessoa=${c.pessoaId}`}>Ver a conta</a>
            </p>
          )}
          {pago && (
            <p className="pr-pago-nota">
              Pago em{' '}
              {new Date(pago.pagoEm).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })} por{' '}
              {pago.pagoPor} · {reais(Number(pago.valor))}
            </p>
          )}
          {resultado && !resultado.ok && (
            <div className={resultado.incerto ? 'pr-incerto' : 'pr-recusado'} role="alert">
              {resultado.mensagem}
            </div>
          )}
        </div>
        <div className="pr-gaveta-rodape">
          <a
            className="pr-botao-largo"
            href={linkDasVendas(c.pessoaId, semana.periodo.de, semana.periodo.ate, hojeBrasilia())}
          >
            Ver as {vendas} vendas
          </a>
          <button
            type="button"
            className="pr-botao-largo pr-forte"
            onClick={() => setAcao({ tipo: 'comprovante' })}
          >
            <Icone nome="down" tamanho={15} traco={1.7} />
            Comprovante
          </button>
        </div>
        {podePagar && !pago && c.aReceber > 0 && fechada && leituraPendente && (
          <div className="pr-incerto pr-gaveta-motivo" role="status">
            {leituraPendente}
          </div>
        )}
        {podePagar && (
          <div className="pr-gaveta-pagar">
            {pago ? (
              <button
                type="button"
                className="pr-botao-largo"
                onClick={() => setAcao({ tipo: 'estornar' })}
              >
                Estornar pagamento
              </button>
            ) : (
              <button
                type="button"
                className="pr-botao-largo pr-botao-vermelho"
                disabled={trabalhando || c.aReceber <= 0 || naoPaga !== null}
                title={naoPaga ?? undefined}
                onClick={() => {
                  setResultado(null);
                  setAcao({ tipo: 'pagar' });
                }}
              >
                {trabalhando
                  ? 'Registrando…'
                  : !fechada
                    ? 'Semana ainda aberta'
                    : c.aReceber <= 0
                      ? 'Nada a pagar'
                      : leituraPendente
                        ? 'Aguardando a leitura do HOS'
                        : `Pagar ${reais(c.aReceber)}`}
              </button>
            )}
          </div>
        )}
      </aside>

      {acao?.tipo === 'liberar' && (
        <PedeMotivo
          titulo={`Liberar ${acao.nome}`}
          texto="A linha passa a pagar o que o app gravou. Fica registrado quem liberou e por quê."
          minimo={3}
          rotulo="Liberar"
          aoFechar={() => setAcao(null)}
          aoConfirmar={async (motivo) => {
            // Um POST por venda da linha. Recusa numa venda não impede as outras; resposta
            // INCERTA (rede) para — não sabemos se gravou. Recarrega sempre: o que já foi
            // liberado tem que aparecer.
            let liberadas = 0;
            let falha: Extract<Escrita, { ok: false }> | null = null;
            for (const id of acao.ids) {
              const r = await liberar(sessao, id, motivo);
              if (r.ok) liberadas += 1;
              else {
                falha = r;
                if (r.incerto) break;
              }
            }
            aoMudar();
            const resumo = falha
              ? resumoDaLiberacao(liberadas, acao.ids.length, falha.mensagem)
              : null;
            if (!resumo || !falha) {
              setAcao(null);
              return { ok: true };
            }
            return { ok: false, incerto: falha.incerto, mensagem: resumo };
          }}
        />
      )}
      {acao?.tipo === 'desfazerCorte' && (
        <PedeMotivo
          titulo={`Desfazer o corte de ${mesPorExtenso(acao.mes)}`}
          texto="A vitamina do mês volta a pagar nas semanas ainda não pagas. Fica registrado quem desfez e por quê. Se um pagamento já descontou a vitamina desse mês, o desconto fica nele (estorne esse pagamento para devolver)."
          minimo={5}
          rotulo="Desfazer corte"
          aoFechar={() => setAcao(null)}
          aoConfirmar={async (motivo) => {
            const r = await desfazerCorte(sessao, acao.id, motivo);
            if (r.ok) {
              setAcao(null);
              aoMudar();
            }
            return r;
          }}
        />
      )}
      {acao?.tipo === 'estornar' && pago && (
        <PedeMotivo
          titulo="Estornar pagamento"
          texto={`O pagamento de ${reais(Number(pago.valor))} fica registrado como estornado — não é apagado.`}
          minimo={5}
          rotulo="Estornar"
          perigo
          aoFechar={() => setAcao(null)}
          aoConfirmar={async (motivo) => {
            const r = await estornar(sessao, pago.id, motivo);
            if (r.ok) {
              setAcao(null);
              aoMudar();
            }
            return r;
          }}
        />
      )}
      {acao?.tipo === 'pagar' && (
        <div className="pr-modal-fundo" role="dialog" aria-label="Confirmar pagamento">
          <div className="pr-modal">
            <h3>Confirmar pagamento</h3>
            <p>
              <strong>{c.nome}</strong> · {reais(c.aReceber)} · semana{' '}
              {semanaCurta(semana.periodo.de, semana.periodo.ate)}
            </p>
            <p>O pagamento fica registrado com o seu nome. Para desfazer, só com estorno.</p>
            <div className="pr-modal-acoes">
              <button
                type="button"
                className="pr-botao"
                onClick={() => setAcao(null)}
                disabled={trabalhando}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="pr-botao pr-botao-vermelho"
                disabled={trabalhando}
                onClick={() => void pagarAgora()}
                autoFocus
              >
                {trabalhando ? 'Registrando…' : 'Confirmar pagamento'}
              </button>
            </div>
          </div>
        </div>
      )}
      {acao?.tipo === 'comprovante' && (
        <Comprovante c={c} periodo={semana.periodo} pago={pago} aoFechar={() => setAcao(null)} />
      )}
    </>
  );
}

function PedeMotivo({
  titulo,
  texto,
  minimo,
  rotulo,
  perigo = false,
  aoFechar,
  aoConfirmar,
}: {
  titulo: string;
  texto: string;
  minimo: number;
  rotulo: string;
  perigo?: boolean;
  aoFechar: () => void;
  aoConfirmar: (motivo: string) => Promise<Escrita>;
}) {
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  return (
    <div className="pr-modal-fundo" role="dialog" aria-label={titulo}>
      <div className="pr-modal">
        <h3>{titulo}</h3>
        <p>{texto}</p>
        <label className="pr-campo">
          <span>Motivo</span>
          <textarea
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            rows={3}
            maxLength={500}
            autoFocus
          />
        </label>
        {erro && <div className="pr-recusado">{erro}</div>}
        <div className="pr-modal-acoes">
          <button type="button" className="pr-botao" onClick={aoFechar} disabled={enviando}>
            Voltar
          </button>
          <button
            type="button"
            className={`pr-botao ${perigo ? 'pr-botao-perigo' : 'pr-botao-vermelho'}`}
            disabled={enviando || motivo.trim().length < minimo}
            onClick={() => {
              setEnviando(true);
              setErro(null);
              void aoConfirmar(motivo.trim()).then((r) => {
                setEnviando(false);
                if (!r.ok) setErro(r.mensagem);
              });
            }}
          >
            {enviando ? 'Registrando…' : rotulo}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Registrar pagamento (lote): um POST por pessoa, com o desfecho de cada um
 * ------------------------------------------------------------------ */

function ModalDePagamento({
  semana,
  sessao,
  aoFechar,
  aoTerminar,
}: {
  semana: ChequeDaSemana;
  sessao: Sessao;
  aoFechar: () => void;
  aoTerminar: () => void;
}) {
  const pagaveis = semana.pessoas.filter((p) => p.aReceber > 0 && !semana.pagamentos[p.pessoaId]);
  const [marcados, setMarcados] = useState<ReadonlySet<string>>(
    () => new Set(pagaveis.map((p) => p.pessoaId)),
  );
  const [resultados, setResultados] = useState<Record<string, Escrita | 'enviando'>>({});
  const [rodando, setRodando] = useState(false);
  const total = pagaveis
    .filter((p) => marcados.has(p.pessoaId))
    .reduce((s, p) => s + p.aReceber, 0);
  const terminou = Object.keys(resultados).length > 0 && !rodando;

  const executar = async () => {
    setRodando(true);
    for (const p of pagaveis) {
      if (!marcados.has(p.pessoaId)) continue;
      setResultados((r) => ({ ...r, [p.pessoaId]: 'enviando' }));
      const r = await pagar(sessao, p.pessoaId, semana.periodo.de, semana.periodo.ate);
      setResultados((x) => ({ ...x, [p.pessoaId]: r }));
    }
    setRodando(false);
  };

  return (
    <div className="pr-modal-fundo" role="dialog" aria-label="Registrar pagamento">
      <div className="pr-modal pr-modal-largo">
        <h3>Registrar pagamento · semana {semanaCurta(semana.periodo.de, semana.periodo.ate)}</h3>
        <p>
          O valor de cada pessoa é recalculado no servidor na hora de pagar. Pagamento não se apaga:
          corrigir é estorno.
        </p>
        <ul className="pr-pagaveis">
          {pagaveis.map((p) => {
            const r = resultados[p.pessoaId];
            return (
              <li key={p.pessoaId}>
                <label>
                  <input
                    type="checkbox"
                    checked={marcados.has(p.pessoaId)}
                    disabled={rodando || terminou}
                    onChange={(e) =>
                      setMarcados((m) => {
                        const n = new Set(m);
                        if (e.target.checked) n.add(p.pessoaId);
                        else n.delete(p.pessoaId);
                        return n;
                      })
                    }
                  />
                  <span>{p.nome}</span>
                </label>
                <strong>{reais(p.aReceber)}</strong>
                <em
                  className={
                    r === undefined
                      ? ''
                      : r === 'enviando'
                        ? 'pr-e-enviando'
                        : r.ok
                          ? 'pr-e-ok'
                          : r.incerto
                            ? 'pr-e-incerto'
                            : 'pr-e-recusado'
                  }
                >
                  {r === undefined
                    ? ''
                    : r === 'enviando'
                      ? 'pagando…'
                      : r.ok
                        ? 'pago'
                        : r.incerto
                          ? 'não confirmado — confira'
                          : r.mensagem}
                </em>
              </li>
            );
          })}
        </ul>
        <div className="pr-modal-total">
          <span>Total selecionado</span>
          <strong>{reais(total)}</strong>
        </div>
        <div className="pr-modal-acoes">
          {terminou ? (
            <button type="button" className="pr-botao pr-botao-vermelho" onClick={aoTerminar}>
              Concluir
            </button>
          ) : (
            <>
              <button type="button" className="pr-botao" onClick={aoFechar} disabled={rodando}>
                Voltar
              </button>
              <button
                type="button"
                className="pr-botao pr-botao-vermelho"
                disabled={rodando || marcados.size === 0}
                onClick={() => void executar()}
              >
                {rodando
                  ? 'Registrando…'
                  : `Pagar ${String(marcados.size)} ${marcados.size === 1 ? 'pessoa' : 'pessoas'}`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Comprovante (o "cheque" do 1.0): o que entrou, o que saiu, e por quê
 * ------------------------------------------------------------------ */

export function Comprovante({
  c,
  periodo,
  pago,
  aoFechar,
}: {
  c: ChequeDaPessoa;
  periodo: { readonly de: string; readonly ate: string };
  pago: Pagamento | null;
  aoFechar: () => void;
}) {
  return (
    <div className="pr-modal-fundo pr-imprimivel" role="dialog" aria-label="Comprovante da semana">
      <div className="pr-modal pr-comprovante">
        <div className="pr-comprovante-topo">
          <img src="/img/inovapay-logo.png" alt="InovaPay" />
          <span>Comprovante da premiação</span>
        </div>
        <div className="pr-comprovante-quem">
          <strong>{c.nome}</strong> · cód {c.codigo ?? '—'} · semana{' '}
          {semanaCurta(periodo.de, periodo.ate)}
        </div>
        <table data-sem-ordenar className="pr-composicao">
          <tbody>
            {composicao(c).map((l, i) => (
              <tr key={i} className={l.desconto ? 'pr-linha-desconto' : ''}>
                <td className="pr-esq">{l.produto}</td>
                <td>{l.qtd}</td>
                <td className="pr-esq pr-regra">{l.regra}</td>
                <td className="pr-gerado">{reais(l.gerado)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="pr-comprovante-contas">
          <div>
            <span>Rendeu</span>
            <b>{reais(c.rendeuBruto)}</b>
          </div>
          <div>
            <span>Canceladas</span>
            <b>{c.cancelado > 0 ? `−${reais(c.cancelado)}` : reais(0)}</b>
          </div>
          <div>
            <span>Devoluções</span>
            <b>{c.devolucao > 0 ? `−${reais(c.devolucao)}` : reais(0)}</b>
          </div>
          {(c.cortadoNoMes ?? 0) + (c.descontoDeCorte ?? 0) > 0 && (
            <div>
              <span>Vitamina do mês cortada</span>
              <b>−{reais((c.cortadoNoMes ?? 0) + (c.descontoDeCorte ?? 0))}</b>
            </div>
          )}
          <div>
            <span>Meta ({c.meta} itens)</span>
            <b>
              {c.metaBatida
                ? `batida (${String(c.itensLiquidos)})`
                : `não batida (${String(c.itensLiquidos)})`}
            </b>
          </div>
          <div className="pr-comprovante-total">
            <span>A receber</span>
            <b>{reais(c.aReceber)}</b>
          </div>
        </div>
        <p className="pr-comprovante-extenso">
          Pague por este comprovante a quantia de <b>{valorPorExtenso(c.aReceber)}</b> a{' '}
          <b>{c.nome}</b> · cód {c.codigo ?? '—'}
        </p>
        <div className="pr-comprovante-assinaturas">
          <span>{dataDoRecibo()}</span>
          <div>
            <i />
            entregue por
          </div>
          <div>
            <i />
            recebi — assinatura
          </div>
        </div>
        <p className="pr-comprovante-pe">
          {pago
            ? `Pago em ${new Date(pago.pagoEm).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })} por ${pago.pagoPor}. Nº ${pago.id.slice(0, 8)}.`
            : 'Ainda não pago.'}{' '}
          {EMPRESA.razao} · CNPJ {EMPRESA.cnpj}. Não é cheque bancário: comprovante interno de
          premiação do programa InovaPay.
        </p>
        <div className="pr-modal-acoes pr-nao-imprime">
          <button type="button" className="pr-botao" onClick={aoFechar}>
            Fechar
          </button>
          <a
            className="pr-botao"
            href={`https://wa.me/?text=${encodeURIComponent(textoDoComprovante(c, periodo, pago))}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Enviar no WhatsApp
          </a>
          <button
            type="button"
            className="pr-botao pr-botao-vermelho"
            onClick={() => window.print()}
          >
            Imprimir
          </button>
        </div>
      </div>
    </div>
  );
}

/** O comprovante em texto, para mandar no WhatsApp (o 1.0 tinha): os mesmos números da tela. */
function textoDoComprovante(
  c: ChequeDaPessoa,
  periodo: { readonly de: string; readonly ate: string },
  pago: Pagamento | null,
): string {
  const menos = (v: number) => (v > 0 ? `−${reais(v)}` : reais(0));
  return [
    '*Comprovante da premiação InovaPay*',
    `${c.nome} · cód ${c.codigo ?? '—'} · semana ${semanaCurta(periodo.de, periodo.ate)}`,
    '',
    `Rendeu: ${reais(c.rendeuBruto)}`,
    `Canceladas: ${menos(c.cancelado)}`,
    `Devoluções: ${menos(c.devolucao)}`,
    `Meta (${String(c.meta)} itens): ${c.metaBatida ? 'batida' : 'não batida'} (${String(c.itensLiquidos)})`,
    `*A receber: ${reais(c.aReceber)}*`,
    pago
      ? `Pago em ${new Date(pago.pagoEm).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}.`
      : 'Ainda não pago.',
  ].join('\n');
}

/* ------------------------------------------------------------------ *
 * Pagamentos anteriores — o histórico entre semanas (1.0)
 * ------------------------------------------------------------------ */

function PagamentosAnteriores({ irSemana }: { irSemana: (de: string) => void }) {
  const [lista, setLista] = useState<readonly PagamentoDoHistorico[] | null>(null);
  const [falhou, setFalhou] = useState(false);
  const [aberto, setAberto] = useState(false);
  const [todos, setTodos] = useState(false);
  const carregar = useCallback(() => {
    setFalhou(false);
    void lerHistoricoDePagamentos().then((r) =>
      r.ok ? setLista(r.dados.pagamentos) : setFalhou(true),
    );
  }, []);
  useEffect(() => {
    if (aberto && !lista) carregar();
  }, [aberto, lista, carregar]);
  const visiveis = lista ? (todos ? lista : lista.slice(0, 15)) : [];
  const quando = (iso: string) =>
    new Date(iso).toLocaleString('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  return (
    <div className="pr-historico">
      <button
        type="button"
        className="pr-historico-abrir"
        aria-expanded={aberto}
        onClick={() => setAberto(!aberto)}
      >
        <span>
          <b>Pagamentos anteriores</b> · o que já foi pago, semana a semana, com os estornos
        </span>
        <span
          className={aberto ? 'pr-historico-seta pr-historico-seta-aberta' : 'pr-historico-seta'}
        >
          <Icone nome="chev-r" tamanho={14} traco={1.8} />
        </span>
      </button>
      {aberto &&
        (falhou ? (
          <ErroDeArea oQue="o histórico de pagamentos" aoTentar={carregar} />
        ) : !lista ? (
          <div className="pr-historico-carregando" aria-busy="true" />
        ) : lista.length === 0 ? (
          <p className="pr-nada">Nenhum pagamento registrado ainda.</p>
        ) : (
          <>
            <table className="pr-historico-tabela">
              <thead>
                <tr>
                  <th>Pago em</th>
                  <th>Balconista</th>
                  <th>Semana</th>
                  <th className="pr-dir">Valor</th>
                  <th>Pago por</th>
                  <th>Situação</th>
                </tr>
              </thead>
              <tbody>
                {visiveis.map((g) => (
                  <tr key={g.id} className={g.estorno ? 'pr-historico-estornado' : ''}>
                    <td className="pr-mono">{quando(g.pagoEm)}</td>
                    <td>{g.pessoa}</td>
                    <td>
                      <button
                        type="button"
                        className="pr-link"
                        onClick={() => irSemana(g.periodo.de)}
                      >
                        {semanaCurta(g.periodo.de, g.periodo.ate)}
                      </button>
                    </td>
                    <td className="pr-dir pr-forte">{reais(Number(g.valor))}</td>
                    <td>{g.pagoPor ?? '—'}</td>
                    <td>
                      {g.estorno ? (
                        <span title={g.estorno.motivo}>
                          Estornado em {quando(g.estorno.em)}
                          {g.estorno.por ? ` por ${g.estorno.por}` : ''}
                        </span>
                      ) : (
                        'Pago'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!todos && lista.length > 15 && (
              <button type="button" className="pr-historico-mais" onClick={() => setTodos(true)}>
                Ver todos ({lista.length})
              </button>
            )}
          </>
        ))}
    </div>
  );
}
